import { useMemo, useRef, useState } from 'react';
import { useEmployeeService } from 'domain/shared/employee';
import { ACTIVE_STATUS as ACTIVE_ASSIGNMENT_STATUS, useAssignmentService } from 'domain/shared/assignment';
import { useNewProjectService } from 'domain/shared/newProject';
import { usePeopleManagementApi } from 'infrastructure/api';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { mapAudienceEmployeesToCoverage } from './complianceAudienceCoverageHelper';
import {
  buildAudienceEmployeeSearchFilter,
  getUniqueStringValues,
  splitAssignmentIdsByAudience,
} from './complianceAudienceCoverageServiceHelper';
import {
  ComplianceAudienceEmployee,
  ComplianceCoverageEmployee,
  AudienceEmployeeSearchResultItem,
  AssignmentSearchResponse,
  AudienceEmployeeSearchResponse,
  ComplianceDocumentMatchParams,
  UseComplianceAudienceCoverageServiceParams,
  ComplianceWorkersResponse,
  ModalLoadingState,
  ComplianceCoverageStatus,
} from './types';
import {
  ASSIGNMENTS_SEARCH_PAGE_SIZE,
  COMPLIANCE_AUDIENCE_COVERAGE_BATCH_SIZE,
  PROJECT_SEARCH_PAGE_SIZE,
} from './constants';

const getInitialCoveragePaginationState = (): ModalLoadingState => ({
  nextPageNumber: 0,
  totalPages: 1,
});

const getInitialLegacyPaginationState = () => ({
  nextEmployeePage: 0,
  totalEmployeePages: 1,
  pendingAudienceEmployees: [] as ComplianceAudienceEmployee[],
  assignmentAliases: null as Nullable<Set<string>>,
});

const useComplianceAudienceCoverageService = ({
  complianceName,
  currentDocumentPath,
  version,
  shouldLoadStatuses,
  useLegacyAudienceWorkers = false,
  relatedEmployeeFilter,
  relatedEmployeeContractTypes,
  assignments = [],
}: UseComplianceAudienceCoverageServiceParams) => {
  const { searchSuggestions: searchEmployees, getFullName } = useEmployeeService();
  const { search: searchAssignments } = useAssignmentService();
  const { searchBase: searchProjects } = useNewProjectService();
  const { sendPostRequest } = usePeopleManagementApi();
  const { addMessage } = useMessageService();

  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [coverageEntries, setCoverageEntries] = useState<ComplianceCoverageEmployee[]>([]);
  const [hasMoreCoverageEntries, setHasMoreCoverageEntries] = useState(false);

  const coveragePaginationStateRef = useRef<ModalLoadingState>(getInitialCoveragePaginationState());
  const legacyPaginationStateRef = useRef(getInitialLegacyPaginationState());
  const isLoadingMoreRef = useRef(false);

  const { relatedProjectIds, relatedAccountIds } = useMemo(
    () => splitAssignmentIdsByAudience(assignments),
    [assignments],
  );
  const documentMatchParams = useMemo<ComplianceDocumentMatchParams>(
    () => ({
      complianceName,
      currentDocumentPath,
      currentDocumentVersion: version,
    }),
    [complianceName, currentDocumentPath, version],
  );

  const employeeSearchFilter = useMemo(
    () => buildAudienceEmployeeSearchFilter(relatedEmployeeFilter, relatedEmployeeContractTypes),
    [relatedEmployeeContractTypes, relatedEmployeeFilter],
  );

  const resetCoverage = () => {
    coveragePaginationStateRef.current = getInitialCoveragePaginationState();
    setCoverageEntries([]);
    setHasMoreCoverageEntries(false);
  };

  const loadCoveragePage = async () => {
    const pageNumber = coveragePaginationStateRef.current.nextPageNumber;
    const response = await sendPostRequest('/compliance/workers', {
      compliancePath: currentDocumentPath,
      pageNumber,
      pageSize: COMPLIANCE_AUDIENCE_COVERAGE_BATCH_SIZE,
    });
    const data: ComplianceWorkersResponse = await response.json();
    const responsePageNumber = Number(data.pageNumber ?? pageNumber);
    const totalPages = Number(data.totalPages || 0);

    coveragePaginationStateRef.current.nextPageNumber = responsePageNumber + 1;
    coveragePaginationStateRef.current.totalPages = totalPages;

    return {
      entries: (data.result || []).map(worker => ({
        employeeAlias: worker.alias,
        employeeFullName: worker.name,
        employeePositionId: 0,
        status: shouldLoadStatuses
          ? worker.isSigned
            ? ComplianceCoverageStatus.SIGNED
            : ComplianceCoverageStatus.NOT_SIGNED
          : ComplianceCoverageStatus.UNAVAILABLE,
      })),
      hasMore: responsePageNumber + 1 < totalPages,
    };
  };

  const mapAudienceEmployee = (employee: AudienceEmployeeSearchResultItem): ComplianceAudienceEmployee => ({
    employeeAlias: employee.alias,
    employeeFullName: getFullName(employee.nameEn),
    employeePositionId: employee.positionId || 0,
  });

  const loadRelatedProjectIds = async () => {
    const [projectsByLegacyId, projectsByAccountId] = await Promise.all([
      relatedProjectIds.length
        ? searchProjects({
            pageNumber: 0,
            pageSize: PROJECT_SEARCH_PAGE_SIZE,
            filter: {
              legacyId: {
                in: relatedProjectIds,
              },
            },
          })
        : Promise.resolve({ result: [] }),
      relatedAccountIds.length
        ? searchProjects({
            pageNumber: 0,
            pageSize: PROJECT_SEARCH_PAGE_SIZE,
            filter: {
              accountId: {
                in: relatedAccountIds,
              },
            },
          })
        : Promise.resolve({ result: [] }),
    ]);

    return getUniqueStringValues(
      [...(projectsByLegacyId.result || []), ...(projectsByAccountId.result || [])].map(project => project.legacyId),
    );
  };

  const ensureLegacyAssignmentAliases = async () => {
    if (!relatedProjectIds.length && !relatedAccountIds.length) {
      return null;
    }

    if (legacyPaginationStateRef.current.assignmentAliases) {
      return legacyPaginationStateRef.current.assignmentAliases;
    }

    const projectIds = await loadRelatedProjectIds();
    const assignmentAliases = new Set<string>();
    let pageNumber = 0;
    let totalPages = 1;

    while (pageNumber < totalPages && projectIds.length) {
      const response: AssignmentSearchResponse = await searchAssignments({
        pageNumber,
        pageSize: ASSIGNMENTS_SEARCH_PAGE_SIZE,
        filter: {
          status: {
            in: [ACTIVE_ASSIGNMENT_STATUS],
          },
          'project.id': {
            in: projectIds,
          },
        },
      });

      (response.result || []).forEach(assignment => {
        if (assignment.employeeId) {
          assignmentAliases.add(String(assignment.employeeId));
        }
      });

      totalPages = Number(response.totalPages || 1);
      pageNumber += 1;
    }

    legacyPaginationStateRef.current.assignmentAliases = assignmentAliases;
    return assignmentAliases;
  };

  const loadLegacyAudienceEmployeesBatch = async ({ reset = false } = {}) => {
    if (reset) {
      legacyPaginationStateRef.current = getInitialLegacyPaginationState();
    }

    const assignmentAliases = await ensureLegacyAssignmentAliases();
    const batch = [...legacyPaginationStateRef.current.pendingAudienceEmployees];

    if (assignmentAliases?.size === 0) {
      return { result: [], hasMore: false };
    }

    while (
      batch.length < COMPLIANCE_AUDIENCE_COVERAGE_BATCH_SIZE &&
      legacyPaginationStateRef.current.nextEmployeePage < legacyPaginationStateRef.current.totalEmployeePages
    ) {
      const response: AudienceEmployeeSearchResponse = await searchEmployees({
        pageNumber: legacyPaginationStateRef.current.nextEmployeePage,
        filter: employeeSearchFilter,
        skipDefaultEmployeeFilters: true,
      });
      legacyPaginationStateRef.current.nextEmployeePage += 1;
      legacyPaginationStateRef.current.totalEmployeePages = Number(response.totalPages || 0);
      batch.push(
        ...(response.result || [])
          .filter(employee => !assignmentAliases || assignmentAliases.has(String(employee.alias)))
          .map(mapAudienceEmployee),
      );
    }

    const result = batch.slice(0, COMPLIANCE_AUDIENCE_COVERAGE_BATCH_SIZE);
    legacyPaginationStateRef.current.pendingAudienceEmployees = batch.slice(COMPLIANCE_AUDIENCE_COVERAGE_BATCH_SIZE);

    return {
      result,
      hasMore:
        legacyPaginationStateRef.current.pendingAudienceEmployees.length > 0 ||
        legacyPaginationStateRef.current.nextEmployeePage < legacyPaginationStateRef.current.totalEmployeePages,
    };
  };

  const mapLegacyAudienceEmployees = (employees: ComplianceAudienceEmployee[]) =>
    mapAudienceEmployeesToCoverage(employees, [], documentMatchParams, ComplianceCoverageStatus.UNAVAILABLE);

  const openCoverage = async () => {
    setLoading(true);
    setLoadingMore(false);

    if (!currentDocumentPath) {
      resetCoverage();
      setLoading(false);
      return;
    }

    try {
      if (useLegacyAudienceWorkers) {
        const audienceEmployeesResponse = await loadLegacyAudienceEmployeesBatch({ reset: true });
        setCoverageEntries(mapLegacyAudienceEmployees(audienceEmployeesResponse.result));
        setHasMoreCoverageEntries(audienceEmployeesResponse.hasMore);
      } else {
        coveragePaginationStateRef.current = getInitialCoveragePaginationState();
        const coverageResponse = await loadCoveragePage();
        setCoverageEntries(coverageResponse.entries);
        setHasMoreCoverageEntries(coverageResponse.hasMore);
      }
    } catch {
      resetCoverage();
      addMessage(new ErrorMessage('Failed to load covered people.'));
    } finally {
      setLoading(false);
    }
  };

  const loadMoreCoverage = async () => {
    if (isLoadingMoreRef.current || !hasMoreCoverageEntries) {
      return;
    }

    isLoadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      if (useLegacyAudienceWorkers) {
        const audienceEmployeesResponse = await loadLegacyAudienceEmployeesBatch();
        setCoverageEntries(prev => [...prev, ...mapLegacyAudienceEmployees(audienceEmployeesResponse.result)]);
        setHasMoreCoverageEntries(audienceEmployeesResponse.hasMore);
      } else {
        const coverageResponse = await loadCoveragePage();
        setCoverageEntries(prev => [...prev, ...coverageResponse.entries]);
        setHasMoreCoverageEntries(coverageResponse.hasMore);
      }
    } catch {
      addMessage(new ErrorMessage('Failed to load covered people.'));
    } finally {
      setLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  };

  return {
    loading,
    isLoadingMore,
    coverageEntries,
    hasMoreCoverageEntries,
    openCoverage,
    loadMoreCoverage,
  };
};

export default useComplianceAudienceCoverageService;
